import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateRange, RangeType } from 'app/trello/models/date-range';
import { PrimeNg } from 'app/trello/primeng.imports';
import { TrelloService } from 'app/trello/services/trello.service';
import { DateTime } from 'luxon';
import { Popover } from 'primeng/popover';

@Component({
  imports: [
    FormsModule,
    PrimeNg,

  ],
  providers: [
    DatePipe,
  ],
  selector: 'app-report-range-selector',
  styleUrls: ['./report-range-selector.component.css'],
  templateUrl: './report-range-selector.component.html',
})
export class ReportRangeSelectorComponent implements OnInit {

  @Input() initialDateRange: DateRange | undefined;
  @Input() emitInitialDateRange: boolean = false;

  @Output() selected = new EventEmitter<DateRange>();

  @ViewChild('op') protected popover!: Popover;

  private readonly fixedRanges: { [key: string]: string } = {
    //
    'lastDays7': 'Last 7 days',
    'lastDays14': 'Last 14 days',
    'lastDays28': 'Last 28 days',
    'lastYear': 'Last year',
    //
    'weekSoFar': 'This week so far',
    'monthSoFar': 'This month so far',
    'yearSoFar': 'This year so far',
    //
    'previousWeek': 'Previous week',
    'previousMonth': 'Previous month',
    'previousYear': 'Previous year',
  };

  private numberOfSelectedDatesInRange: number = 0;

  protected selectedRangeDates: Date[] | undefined;
  protected selectedFixedRangeValue: string = 'weekSoFar';
  protected selectedFixedRangeLabel: string = this.fixedRanges[this.selectedFixedRangeValue];

  constructor(
    private readonly datePipe: DatePipe,
    private readonly trelloService: TrelloService, // required
  ) {
  }

  public ngOnInit() {

    if (this.initialDateRange) {

      if (this.initialDateRange.rangeType === 'custom') {

        const rangeFrom = DateTime.fromISO(this.initialDateRange.rangeFrom!, {setZone: true});
        const rangeTo = DateTime.fromISO(this.initialDateRange.rangeTo!, {setZone: true});

        if (rangeFrom.equals(rangeFrom.startOf('day'))
          && rangeTo.equals(rangeTo.endOf('day'))) {

          this.selectedRangeDates = [
            DateTime.fromISO(this.initialDateRange.rangeFrom!, {setZone: true}).toJSDate(),
            DateTime.fromISO(this.initialDateRange.rangeTo!, {setZone: true}).toJSDate(),
          ];

          if (this.emitInitialDateRange) {
            this.onDateChange();
          }

          return;

        } else {
          console.warn('Initial date range not whole days, ignoring');
        }
      }

      if (this.emitInitialDateRange) {
        this.selectFixedRange(this.initialDateRange.rangeType);
      } else {
        this.setFixedRange(this.initialDateRange.rangeType);
      }
    }
  }

  protected onDateChange() {

    const rangeFrom = this.selectedRangeDates ? this.selectedRangeDates[0] : null;
    const rangeTo = this.selectedRangeDates ? this.selectedRangeDates[1] : null;

    let selectedFixedRangeLabel = '';

    if (rangeFrom) {
      selectedFixedRangeLabel += this.datePipe.transform(rangeFrom, 'shortDate');
    }

    if (rangeTo) {
      selectedFixedRangeLabel += ' - ' + this.datePipe.transform(rangeTo, 'shortDate');
    }

    this.selectedFixedRangeLabel = selectedFixedRangeLabel;

    const lastNumberOfSelectedDatesInRange = this.numberOfSelectedDatesInRange;

    this.numberOfSelectedDatesInRange = 0;
    this.numberOfSelectedDatesInRange += rangeFrom ? 1 : 0;
    this.numberOfSelectedDatesInRange += rangeTo ? 1 : 0;

    if (this.numberOfSelectedDatesInRange == 2
      && this.numberOfSelectedDatesInRange != lastNumberOfSelectedDatesInRange) {

      this.numberOfSelectedDatesInRange = 0;
      this.selectedFixedRangeValue = '';

      this.emitSelection(
        DateTime.fromJSDate(rangeFrom!).startOf('day'),
        DateTime.fromJSDate(rangeTo!).endOf('day'),
        'custom',
      );

      if (this.popover) {
        this.popover.hide();
      }
    }
  }

  protected selectFixedRange(
    fixedRange: string,
  ) {

    this.setFixedRange(fixedRange);

    const now = DateTime.local();

    let rangeFrom: DateTime;
    let rangeTo: DateTime;

    switch (fixedRange) {

      case 'lastDays7':

        rangeFrom = now.minus({days: 7}).startOf('day');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'lastDays14':

        rangeFrom = now.minus({days: 14}).startOf('day');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'lastDays28':

        rangeFrom = now.minus({days: 28}).startOf('day');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'lastYear':

        rangeFrom = now.minus({year: 1}).startOf('day');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'weekSoFar':

        rangeFrom = now.startOf('week');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'monthSoFar':

        rangeFrom = now.startOf('month');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'yearSoFar':

        rangeFrom = now.startOf('year');
        rangeTo = now.minus({days: 1}).endOf('day');

        break;

      case 'previousWeek':

        rangeFrom = now.minus({weeks: 1}).startOf('week');
        rangeTo = now.minus({weeks: 1}).endOf('week');

        break;

      case 'previousMonth':

        rangeFrom = now.minus({months: 1}).startOf('month');
        rangeTo = now.minus({months: 1}).endOf('month');

        break;

      case 'previousYear':

        rangeFrom = now.minus({years: 1}).startOf('year');
        rangeTo = now.minus({years: 1}).endOf('year');

        break;

      default:
        throw new Error(`Unknown fixed range: ${fixedRange}`);
    }

    this.emitSelection(rangeFrom, rangeTo, fixedRange);

    if (this.popover) {
      this.popover.hide();
    }
  }

  private emitSelection(
    rangeFrom: DateTime,
    rangeTo: DateTime,
    rangeType: RangeType,
  ) {

    const selection = {
      rangeFrom: rangeFrom?.toISO()!,
      rangeTo: rangeTo?.toISO()!,
      rangeType: rangeType,
    };

    this.selected.emit(selection);
  }

  private setFixedRange(
    fixedRange: string,
  ) {

    this.selectedFixedRangeValue = fixedRange;
    this.selectedFixedRangeLabel = this.fixedRanges[fixedRange];

    this.selectedRangeDates = undefined;
    this.numberOfSelectedDatesInRange = 0;
  }

}
